import { Injectable, Inject, DestroyRef, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';

import { ApiService } from '@libs/services/api-base';
import { APP_CONFIG } from '@libs/configs';

import { Account } from '../types/account';
import { AuthenticationService } from '@libs/authentication/services';
import {
  AccountNote,
  AccountReview,
  ReviewStatus,
  ReviewStatusReasonEnum,
  UpdateReviewStatus,
} from '../types/account-review';
import { App } from '@libs/types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable()
export class AccountsApiService extends ApiService {
  private readonly accountsUrl = '/edge/accounts';
  private readonly destroyRef = inject(DestroyRef);

  currentAccount$ = new BehaviorSubject<Account>(null);

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    @Inject(APP_CONFIG) private appConfig: any,
  ) {
    super();

    if (this.appConfig.app === App.Employer) {
      this.setAccountfromAuth();
    }
  }

  setAccountfromAuth(): void {
    this.authService.account$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((authAccount) => {
        if (authAccount.id) {
          this.getAccount(authAccount.id).subscribe((account: Account) => {
            this.currentAccount$.next(account);
          });
        }
      });
  }

  getAccount(id: string): Observable<Account> {
    return this.http.get<Account>(`${this.accountsUrl}/${id}`);
  }

  getAccountReview(id: string): Observable<AccountReview> {
    return this.http.get<AccountReview>(`${this.accountsUrl}/${id}/review`);
  }

  updateAccountReviewStatus(
    id: string,
    statusCode: ReviewStatus,
    reasonCode: ReviewStatusReasonEnum,
  ) {
    const update: UpdateReviewStatus = {
      reviewStatus: statusCode,
      reason: reasonCode,
    };

    return this.http.put(`${this.accountsUrl}/${id}/review/status`, update);
  }

  addAccountNote(id: string, text: string): Observable<AccountNote> {
    return this.http.post<AccountNote>(
      `${this.accountsUrl}/${id}/review/note`,
      { text: text },
    );
  }

  /**
   * @deprecated This should only be used in the admin app and not intended to be used in the employer app.
   */
  canEditAccount(): boolean {
    const account = this.currentAccount$.getValue();

    return account == null ? false : this.canEditPlatform(account.platform);
  }

  /**
   * @deprecated This should only be used in the admin app and not intended to be used in the employer app.
   */
  canEditPlatform(platform: string): boolean {
    return platform === 'Marketplace' || platform === 'PeopleMatter';
  }
}
